import { useState } from 'react';
import {
  Page,
  Card,
  TextField,
  Button,
  Modal,
  Toast,
  Frame,
  BlockStack,
  Pagination,
  Text,
  Divider,
  InlineStack,
  Badge,
} from '@shopify/polaris';
import { useApi } from '../hooks/useApi';
import { useShop } from '../hooks/useShop';
import { useCurrency } from '../hooks/useCurrency';
import { client } from '../api/client';
import { Endpoints } from '../api/endpoints';
import ProductTable, { ProductRow } from '../components/ProductTable';
import CostForm, { CostFormValues } from '../components/CostForm';
import BreakevenRoasWidget, { BreakevenVariant } from '../components/BreakevenRoasWidget';

interface CostHistoryRow {
  id: number;
  cost_amount: number | string;
  quantity: number | null;
  supplier: string | null;
  batch_ref: string | null;
  effective_date: string;
}

interface ProductDetail {
  product: ProductRow;
  cost_history: CostHistoryRow[];
  variants?: BreakevenVariant[];
  gateway_fee?: { rate: number; flat: number };
}

interface ProductsResponse {
  data: ProductRow[];
  current_page: number;
  last_page: number;
  total: number;
  per_page: number;
}

const PER_PAGE = 10;

export default function Products() {
  const { shop } = useShop();
  const { formatMoney } = useCurrency(shop?.currency ?? 'USD');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState<ProductRow | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const products = useApi<ProductsResponse>(Endpoints.products.list, {
    search,
    page,
    per_page: PER_PAGE,
  });

  // Current cost + batch history for the selected product (null skips the fetch).
  const detail = useApi<ProductDetail>(
    selected ? Endpoints.products.detail(selected.shopify_product_id) : null,
  );

  const handleSearchChange = (value: string) => {
    setSearch(value);
    setPage(1);
  };

  const handleImport = async () => {
    await client.post(Endpoints.products.import);
    setToast('Product import queued');
  };

  const handleSaveCost = async (values: CostFormValues) => {
    if (!selected) return;
    setSaving(true);
    try {
      await client.post(Endpoints.products.setCost(selected.shopify_product_id), {
        cost_amount: parseFloat(values.cost_amount || '0'),
        supplier: values.supplier || null,
        batch_ref: values.batch_ref || null,
        quantity: values.quantity ? parseInt(values.quantity, 10) : 0,
        effective_date: values.effective_date,
        notes: values.notes || null,
      });
      setToast('Cost saved');
      setSelected(null);
      products.refetch();
    } catch (e) {
      setToast((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Frame>
      <Page
        title="Products"
        primaryAction={{ content: 'Import from Shopify', onAction: handleImport }}
      >
        <Card>
          <BlockStack gap="400">
            <TextField
              label="Search products"
              labelHidden
              placeholder="Search by title"
              value={search}
              onChange={handleSearchChange}
              autoComplete="off"
              connectedRight={<Button onClick={() => products.refetch()}>Search</Button>}
            />
            <ProductTable
              products={products.data?.data ?? []}
              currency={shop?.currency ?? 'USD'}
              onSelect={setSelected}
            />
            {products.data && products.data.last_page > 1 && (
              <Pagination
                hasPrevious={page > 1}
                onPrevious={() => setPage((p) => Math.max(1, p - 1))}
                hasNext={page < products.data.last_page}
                onNext={() => setPage((p) => Math.min(products.data!.last_page, p + 1))}
                label={`${products.data.total} products`}
              />
            )}
          </BlockStack>
        </Card>
      </Page>

      {selected && (
        <Modal
          open
          onClose={() => setSelected(null)}
          title={`Set cost — ${selected.title}`}
          size="large"
        >
          <Modal.Section>
            <BlockStack gap="300">
              <InlineStack gap="200" blockAlign="center">
                <Text as="span" variant="bodyMd" tone="subdued">
                  Current cost
                </Text>
                <Text as="span" variant="headingMd">
                  {selected.default_cost != null ? formatMoney(selected.default_cost) : 'Not set'}
                </Text>
                {shop?.settings?.cost_method ? (
                  <Badge tone="info">{String(shop.settings.cost_method).toUpperCase()}</Badge>
                ) : null}
              </InlineStack>

              <Divider />
              <Text as="h3" variant="headingSm">
                Breakeven ROAS
              </Text>
              <BreakevenRoasWidget
                variants={detail.data?.variants}
                formatMoney={formatMoney}
              />

              {detail.data && detail.data.cost_history.length > 0 && (
                <BlockStack gap="150">
                  <Text as="span" variant="bodySm" tone="subdued">
                    Cost batches (newest first)
                  </Text>
                  {detail.data.cost_history.slice(0, 5).map((c) => (
                    <InlineStack key={c.id} gap="300" blockAlign="center">
                      <Text as="span" variant="bodyMd" fontWeight="semibold">
                        {formatMoney(c.cost_amount)}
                      </Text>
                      <Text as="span" variant="bodySm" tone="subdued">
                        {c.effective_date?.slice(0, 10)}
                        {c.quantity ? ` · qty ${c.quantity}` : ''}
                        {c.supplier ? ` · ${c.supplier}` : ''}
                        {c.batch_ref ? ` · ${c.batch_ref}` : ''}
                      </Text>
                    </InlineStack>
                  ))}
                </BlockStack>
              )}

              <Divider />
              <Text as="h3" variant="headingSm">
                Add a new cost batch
              </Text>
              <CostForm onSubmit={handleSaveCost} saving={saving} />
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}

      {toast && <Toast content={toast} onDismiss={() => setToast(null)} />}
    </Frame>
  );
}
